import React from 'react';
import { TagChip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { LeadAlertTooltip, LeadCardAvatar, LeadCardField, LeadCardFlame } from './LeadCardParts';
import type { LeadCardViewModel } from './leadCardHelpers';

export interface LeadCardVariantProps {
  vm: LeadCardViewModel;
  showAssignee: boolean;
  /** Só o corpo de Detalhes renderiza chips; nas outras densidades a ref fica nula. */
  tagsAreaRef: React.Ref<HTMLSpanElement>;
}

const TITLE: React.CSSProperties = {
  fontSize: 14, fontWeight: 600, lineHeight: 1.35,
  letterSpacing: '-0.02em', color: 'var(--theme-text)',
};
const VALUE: React.CSSProperties = {
  fontSize: 14, fontWeight: 600, color: 'var(--theme-text)',
  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
};
/**
 * O tight do valor é EXCLUSIVO da ficha: o mockup declara
 * `.crm1-ficha .c3-card__value { letter-spacing: tight }` (crm-corrigidos.html:36) —
 * um seletor descendente que só alcança o card de Detalhes. O valor do Normal usa
 * `.c3-card__value` puro, sem tracking.
 */
const VALUE_TIGHT: React.CSSProperties = { ...VALUE, letterSpacing: '-0.02em' };
const SUB: React.CSSProperties = { fontSize: 12, color: 'var(--theme-muted-text)' };
const CHIP: React.CSSProperties = {
  padding: '1px 8px', border: '1px solid var(--theme-input-border)',
  // `.c3-chip` tem fundo SÓLIDO (cards-v3.css §8). Sem ele o chip fica transparente e
  // deixa passar o fundo do card selecionado (`--theme-surface-selected`).
  backgroundColor: 'var(--theme-card-background)',
  borderRadius: 9999, fontSize: 11, fontWeight: 500, lineHeight: '18px',
  color: 'var(--theme-card-text)', whiteSpace: 'nowrap',
};

/**
 * Só a MÉTRICA do chip — sem cor. O `TagChip` traz a cor do catálogo e o
 * fundo opaco; repetir `color`/`backgroundColor` aqui sobrescreveria os dois.
 */
const CHIP_LAYOUT: React.CSSProperties = {
  padding: '1px 8px', borderRadius: 9999,
  fontSize: 11, fontWeight: 500, lineHeight: '18px',
};

/**
 * Chip de resposta do formulário (mockup 12-B): `.tk-chip` SEM `.qr` — mesma paleta
 * neutra do chip de tag. O lilás (`.tk-chip.qr`) é só do badge "Captura QR".
 * (Fix round 1 da Task 11: o brief pedia lilás; o mockup aprovado vence.)
 */
const FORM_CHIP: React.CSSProperties = CHIP;

const FormChips: React.FC<{ chips: string[]; leadId: string }> = ({ chips, leadId }) => (
  chips.length === 0 ? null : (
    <span
      className="flex items-center min-w-0 overflow-hidden"
      style={{ gap: 4, flexWrap: 'nowrap' }}
      data-testid={`lead-form-chips-${leadId}`}
    >
      {chips.slice(0, 2).map((chip) => (
        <span key={chip} className="truncate" style={FORM_CHIP}>{chip}</span>
      ))}
      {chips.length > 2 && (
        <span className="flex-none" style={{ ...FORM_CHIP, fontVariantNumeric: 'tabular-nums' }}>+{chips.length - 2}</span>
      )}
    </span>
  )
);

/** 01 · Ficha de dois campos — cabeçalho + divisor + grade 2×2 rotulada + chips. */
export const LeadCardDetailed: React.FC<LeadCardVariantProps> = ({ vm, showAssignee, tagsAreaRef }) => (
  <>
    {/* `.c3-card__head` com `gap: 6px` (override `.crm1-ficha`) */}
    <div className="flex items-start min-w-0" style={{ gap: 6 }}>
      <LeadCardAvatar
        size={24}
        role="contact"
        name={vm.contactName}
        {...(vm.contactPicture ? { picture: vm.contactPicture } : {})}
        data-testid={`lead-contact-avatar-${vm.id}`}
      />
      <div className="flex-1 min-w-0">
        <h3 className="truncate min-w-0" style={TITLE}>{vm.contactName}</h3>
        {(vm.temperature || vm.customerName) && (
          <div className="flex items-center min-w-0" style={{ ...SUB, gap: 6, marginTop: 2 }}>
            {vm.temperature && <LeadCardFlame temperature={vm.temperature} size={12} leadId={vm.id} />}
            {vm.customerName && <span className="truncate min-w-0">{vm.customerName}</span>}
          </div>
        )}
      </div>
      {vm.budgetLabel && <span className="flex-none" style={VALUE_TIGHT}>{vm.budgetLabel}</span>}
    </div>

    {/* `.c3-sep` — fio de 1px com respiro de 4px */}
    <div style={{ height: 1, margin: '4px 0', backgroundColor: 'var(--theme-card-border)' }} />

    {/* `.crm1-grid` — 2 colunas, gap 12 */}
    <div className="grid min-w-0" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <LeadCardField
        label="Origem · Canal"
        icon={vm.channelIcon}
        value={vm.originChannelLabel}
        color="var(--theme-card-text)"
        iconColor="var(--theme-muted-text)"
        data-testid={`lead-field-origin-${vm.id}`}
      />
      <LeadCardField
        label="Follow-up"
        icon="calendar"
        value={vm.followUp.label}
        color={vm.followUp.color}
        data-testid={`lead-field-followup-${vm.id}`}
      />
      <LeadAlertTooltip vm={vm}>
        <LeadCardField
          label="Parado"
          icon="clock"
          value={vm.stagnation.label}
          color={vm.stagnation.color}
          data-testid={`lead-field-idle-${vm.id}`}
        />
      </LeadAlertTooltip>
      <div className="min-w-0">
        <div
          className="truncate"
          style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
            textTransform: 'uppercase', lineHeight: 1.2, color: 'var(--theme-muted-text)',
          }}
        >
          Responsável
        </div>
        <div
          className="flex items-center min-w-0"
          style={{ marginTop: 4, gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--theme-card-text)' }}
        >
          {/* SEM anel, de propósito: o rótulo "Responsável" logo acima já diz o papel,
              e a fundação §9 dispensa o recorte nesse caso (crm-corrigidos.html:216). */}
          <LeadCardAvatar
            size={20}
            role="assignee"
            {...(showAssignee && vm.assigneeName ? { name: vm.assigneeName } : {})}
            {...(showAssignee && vm.assigneePicture ? { picture: vm.assigneePicture } : {})}
            data-testid={`lead-assignee-avatar-${vm.id}`}
          />
          <span className="truncate min-w-0">{(showAssignee && vm.assigneeFirstName) || '—'}</span>
        </div>
      </div>
    </div>

    <FormChips chips={vm.formChips} leadId={vm.id} />

    {/* `.c3-chips.c3-chips--single` — uma linha, o excedente vira "+N" */}
    {vm.tags.length > 0 && (
      <span
        ref={tagsAreaRef}
        data-tags-area
        data-testid={`lead-tags-area-${vm.id}`}
        className="flex items-center min-w-0 overflow-hidden cursor-pointer"
        style={{ gap: 4, flexWrap: 'nowrap' }}
      >
        {/* `variant="solid"`: o chip do kanban precisa de fundo OPACO — com
            fundo translúcido ele deixa passar o `--theme-surface-selected` e
            muda de cor quando o card é selecionado (cards-v3.css §8). */}
        {vm.tags.slice(0, 2).map((tag) => (
          <TagChip key={tag} name={tag} variant="solid" className="truncate" style={CHIP_LAYOUT} />
        ))}
        {vm.tags.length > 2 && (
          <span
            className="flex-none"
            style={{ ...CHIP, color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
          >
            +{vm.tags.length - 2}
          </span>
        )}
      </span>
    )}
  </>
);

/** 02 · Coluna de pessoas — calha de 44px só com gente + corpo do negócio. */
export const LeadCardNormal: React.FC<LeadCardVariantProps> = ({ vm, showAssignee }) => (
  <>
    {/* `.crm1-two__gutter` — padding 14 no topo/base, gap 12, fio à direita */}
    <div
      className="flex flex-col items-center"
      style={{ gap: 12, padding: '14px 0', borderRight: '1px solid var(--theme-card-border)' }}
    >
      <LeadCardAvatar
        size={28}
        role="contact"
        name={vm.contactName}
        {...(vm.contactPicture ? { picture: vm.contactPicture } : {})}
        data-testid={`lead-contact-avatar-${vm.id}`}
      />
      {showAssignee && (
        /* Anel ligado: a calha não tem rótulo de papel, só posição — o recorte é o
           que diz "responsável" sem depender de cor (crm-corrigidos.html:262). */
        <LeadCardAvatar
          size={24}
          role="assignee"
          ring
          {...(vm.assigneeName ? { name: vm.assigneeName } : {})}
          {...(vm.assigneePicture ? { picture: vm.assigneePicture } : {})}
          data-testid={`lead-assignee-avatar-${vm.id}`}
        />
      )}
    </div>

    {/* `.crm1-two__body` — padding 14, coluna com gap 8 */}
    <div className="min-w-0 flex flex-col" style={{ padding: 14, gap: 8 }}>
      <div className="min-w-0">
        <div className="flex items-center min-w-0" style={{ gap: 6 }}>
          {vm.temperature && <LeadCardFlame temperature={vm.temperature} size={14} leadId={vm.id} />}
          <span className="truncate min-w-0" style={TITLE}>{vm.contactName}</span>
        </div>
        {vm.subtitle && (
          <div className="truncate min-w-0" style={{ ...SUB, marginTop: 2 }}>{vm.subtitle}</div>
        )}
      </div>
      <FormChips chips={vm.formChips} leadId={vm.id} />
      <div className="flex items-center justify-between min-w-0" style={{ gap: 8 }}>
        {/* `flex-none`: sem isso uma meta longa ("silenciado até 12/08") comprime o
            valor, que tem `white-space: nowrap` e vazaria em vez de truncar. */}
        <span className="flex-none" style={VALUE}>{vm.budgetLabel ?? '—'}</span>
        <LeadAlertTooltip vm={vm}>
          <span
            className="inline-flex items-center flex-none"
            data-testid={`lead-meta-${vm.id}`}
            style={{
              gap: 4, fontSize: 12, color: vm.meta.color, whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
              ...(vm.meta.strong ? { fontWeight: 600 } : {}),
            }}
          >
            <Icon name={vm.meta.icon} size={14} />
            {vm.meta.label}
          </span>
        </LeadAlertTooltip>
      </div>
    </div>
  </>
);

/** 03 · Linha viva — quem, quanto e de quem, em uma linha de 50px. */
export const LeadCardCompact: React.FC<LeadCardVariantProps> = ({ vm, showAssignee }) => (
  <>
    <LeadAlertTooltip vm={vm}>
      <span
        className="flex-none"
        data-testid={`lead-dot-${vm.id}`}
        style={{ width: 6, height: 6, borderRadius: 9999, backgroundColor: vm.dotColor }}
      />
    </LeadAlertTooltip>
    {vm.temperature && <LeadCardFlame temperature={vm.temperature} size={12} leadId={vm.id} />}
    <LeadCardAvatar
      size={20}
      role="contact"
      name={vm.contactName}
      {...(vm.contactPicture ? { picture: vm.contactPicture } : {})}
      data-testid={`lead-contact-avatar-${vm.id}`}
    />
    <span
      className="flex-1 min-w-0 truncate"
      style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--theme-text)' }}
    >
      {vm.contactName}
    </span>
    <span
      className="flex-none"
      style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
    >
      {vm.budgetCompact ?? '—'}
    </span>
    <i className="flex-none" style={{ width: 1, height: 14, backgroundColor: 'var(--theme-input-border)' }} />
    {showAssignee && (
      /* Anel ligado: a linha não tem rótulo de papel (crm-corrigidos.html:366). */
      <LeadCardAvatar
        size={20}
        role="assignee"
        ring
        {...(vm.assigneeName ? { name: vm.assigneeName } : {})}
        {...(vm.assigneePicture ? { picture: vm.assigneePicture } : {})}
        data-testid={`lead-assignee-avatar-${vm.id}`}
      />
    )}
  </>
);
